import { makeAutoObservable, runInAction } from 'mobx';

import { cashoutsService } from '@/services/cashouts';
import { newIdempotencyKey } from '@/lib/idempotencyKey';
import { CASHOUT_STATUS, type CashOut } from '@/types/cashout';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60_000;

class CashoutStore {
  activeCashout: CashOut | null = null;
  creating = false;
  createError: string | null = null;
  polling = false;
  openingLink = false;
  linkError: string | null = null;

  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private pollStartedAt = 0;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get isTerminal(): boolean {
    const s = this.activeCashout?.status;
    return s === CASHOUT_STATUS.Ready
      || s === CASHOUT_STATUS.Failed;
  }

  get isTimedOut(): boolean {
    if (!this.polling) return false;
    return Date.now() - this.pollStartedAt >= POLL_TIMEOUT_MS;
  }

  async create(balanceCents: number): Promise<number | null> {
    runInAction(() => {
      this.creating = true;
      this.createError = null;
    });
    try {
      const idempotencyKey = newIdempotencyKey();
      const { cash_out } = await cashoutsService.create(balanceCents, idempotencyKey);
      runInAction(() => {
        this.activeCashout = cash_out;
      });
      return cash_out.id;
    } catch (err) {
      runInAction(() => {
        this.createError = err instanceof Error ? err.message : 'Failed to create cashout.';
      });
      return null;
    } finally {
      runInAction(() => {
        this.creating = false;
      });
    }
  }

  startPolling(id: number): void {
    this.stopPolling();
    runInAction(() => {
      this.polling = true;
      this.pollStartedAt = Date.now();
    });
    this.pollTimer = setInterval(() => {
      void this.pollOnce(id);
    }, POLL_INTERVAL_MS);
    void this.pollOnce(id);
  }

  stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    runInAction(() => {
      this.polling = false;
    });
  }

  async refreshStatus(id: number): Promise<void> {
    try {
      const { cash_out } = await cashoutsService.show(id);
      runInAction(() => {
        this.activeCashout = cash_out;
      });
    } catch {
      // Silent — retry on next poll or manual refresh
    }
  }

  // Fetches a fresh Tremendous redemption URL. Defaults to the active cashout
  // (status screen), but accepts an explicit id so the history screen can
  // reopen any past `ready` cashout's reward — same reward, regenerated link.
  async getRedemptionLink(cashOutId?: number): Promise<string | null> {
    const id = cashOutId ?? this.activeCashout?.id;
    if (!id) return null;
    // Only the active-cashout (status screen) path drives the shared
    // openingLink/linkError observables. When an explicit id is passed (history
    // screen), the caller owns its own per-row loading/error state so a history
    // tap never flips the status screen's button.
    const tracksSharedState = cashOutId === undefined;
    if (tracksSharedState) {
      runInAction(() => {
        this.openingLink = true;
        this.linkError = null;
      });
    }
    try {
      const { redemption_url } = await cashoutsService.getLink(id);
      return redemption_url;
    } catch (err) {
      if (tracksSharedState) {
        runInAction(() => {
          this.linkError = err instanceof Error
            ? err.message
            : 'Reward link unavailable right now.';
        });
      }
      return null;
    } finally {
      if (tracksSharedState) {
        runInAction(() => {
          this.openingLink = false;
        });
      }
    }
  }

  reset(): void {
    this.stopPolling();
    runInAction(() => {
      this.activeCashout = null;
      this.creating = false;
      this.createError = null;
      this.openingLink = false;
      this.linkError = null;
    });
  }

  private async pollOnce(id: number): Promise<void> {
    if (Date.now() - this.pollStartedAt >= POLL_TIMEOUT_MS) {
      this.stopPolling();
      return;
    }
    try {
      const { cash_out } = await cashoutsService.show(id);
      runInAction(() => {
        this.activeCashout = cash_out;
      });
      if (
        cash_out.status === CASHOUT_STATUS.Ready
        || cash_out.status === CASHOUT_STATUS.Failed
      ) {
        this.stopPolling();
      }
    } catch {
      // Silent — retry on next interval
    }
  }
}

export const cashoutStore = new CashoutStore();
export { CashoutStore };
